import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Seo, { orgJsonLd } from "../components/Seo";
import Reveal from "../components/Reveal";
import RevealImage from "../components/RevealImage";
import { projects } from "../data/projects";

const HERO_IMAGES = [
  { src: "/images/chandroday/full/7.jpg", tag: "Chandroday — Bhavnagar", alt: "Chandroday — building as seen from North-East" },
  { src: "/images/veiled-house/full/25.jpg", tag: "The Veiled House — Bhavnagar", alt: "The Veiled House — South elevation, evening" },
  { src: "/images/chandroday/full/20.jpg", tag: "Chandroday — Courtyard", alt: "Chandroday — Courtyard" },
  { src: "/images/veiled-house/full/1.jpg", tag: "The Veiled House — South-East", alt: "The Veiled House — Complete ensemble from South-East" },
];

export default function Home() {
  const [heroIndex, setHeroIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setHeroIndex((v) => (v + 1) % HERO_IMAGES.length);
    }, 6500);
    return () => clearInterval(id);
  }, []);

  const hero = HERO_IMAGES[heroIndex];

  return (
    <>
      <Seo jsonLd={orgJsonLd} />

      {/* HERO */}
      <section className="relative h-[100svh] min-h-[560px] -mt-16 md:-mt-20 w-full overflow-hidden">
        <AnimatePresence mode="sync">
          <motion.img
            key={hero.src}
            src={hero.src}
            alt={hero.alt}
            className="absolute inset-0 w-full h-full object-cover"
            initial={{ opacity: 0, scale: 1.08 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.02 }}
            transition={{ duration: 1.8, ease: [0.33, 1, 0.68, 1] }}
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-b from-ink/30 via-ink/10 to-ink/60" />
        {/* Bottom-right location tag (auto-updates) */}
        <div className="absolute bottom-6 right-6 md:right-16 z-10 rotate-90 md:rotate-0 origin-bottom-right">
          <AnimatePresence mode="wait">
            <motion.div
              key={hero.tag}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.5 }}
              className="text-[10px] tracking-widest3 uppercase text-paper/80"
            >
              {hero.tag}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Tiny progress ticks at bottom-left (one per image) */}
        <div className="absolute bottom-6 left-6 md:left-16 z-10 flex items-center gap-2">
          {HERO_IMAGES.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setHeroIndex(i)}
              aria-label={`Hero image ${i + 1}`}
              className="relative w-8 h-px bg-paper/30 overflow-hidden"
            >
              <span
                className={`absolute inset-y-0 left-0 bg-paper transition-[width] ease-linear ${
                  heroIndex === i ? "w-full duration-[6500ms]" : "w-0 duration-300"
                }`}
              />
            </button>
          ))}
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="container-page py-24 md:py-40">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <Reveal className="md:col-span-4">
            <div className="eyebrow">Studio</div>
          </Reveal>
          <Reveal className="md:col-span-8" delay={0.1}>
            <p className="font-display text-3xl md:text-5xl leading-tight tracking-wide">
              An architectural practice based at Bhavnagar, Gujarat. Founded as a studio by
              Rajanikant Machhar in 1976 — today led by a core team of design &amp; engineering
              associates engaged across a diverse range of projects.
            </p>
            <p className="mt-8 text-base md:text-lg leading-relaxed text-smoke max-w-3xl">
              Our design philosophy is intuitive yet pragmatic — an architecture that responds to
              the geographical & environmental context of a place, to human needs and aspirations,
              to the social & cultural context that define a place, to space management & economics
              and usage of appropriate technology leading to built-form that befits a place and its
              purpose.
            </p>
            <Link to="/about" className="btn-link mt-10">
              <span>About the studio</span>
              <span aria-hidden>→</span>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* APPROACH */}
      <section className="border-t border-mist">
        <div className="container-page py-20 md:py-32">
          <Reveal className="max-w-2xl mb-14 md:mb-20">
            <div className="eyebrow mb-4">Approach</div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-6xl leading-[0.95] tracking-normal sm:tracking-widest2">
              The way<br />we work.
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-6">
            {[
              { n: "01", t: "Observe", d: "We begin with the site. Its orientation, climate, soil, neighbours, and memories." },
              { n: "02", t: "Listen", d: "The client's hopes and constraints are drawn alongside our own." },
              { n: "03", t: "Draft", d: "Many iterations at the sketch; few lines at the drawing." },
              { n: "04", t: "Build", d: "The smallest means to the largest effect — restraint as discipline." },
            ].map((step, i) => (
              <Reveal key={step.n} delay={i * 0.08}>
                <div className="border-t border-mist pt-6 pr-4">
                  <div className="font-display text-5xl md:text-6xl tracking-widest2 leading-none">
                    {step.n}
                  </div>
                  <div className="mt-5 text-xs tracking-widest2 uppercase text-ink">
                    {step.t}
                  </div>
                  <p className="mt-4 text-sm leading-relaxed text-smoke">
                    {step.d}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SELECTED WORKS */}
      <section className="container-page pt-24 md:pt-40 pb-24 md:pb-40">
        <Reveal className="flex items-end justify-between mb-10 md:mb-16">
          <div>
            <div className="eyebrow mb-3">Selected Works</div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-6xl tracking-normal sm:tracking-widest2">Projects</h2>
          </div>
          <Link to="/works" className="btn-link hidden md:inline-flex">
            <span>All works</span>
            <span aria-hidden>→</span>
          </Link>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
          {projects.map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.1}>
              <Link to={`/works/${p.slug}`} className="group block">
                <RevealImage aspect="aspect-[4/5]" delay={i * 0.08}>
                  <img
                    src={`/images/${p.slug}/full/${p.cover}.jpg`}
                    alt={p.title}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-smooth group-hover:scale-[1.04]"
                  />
                  <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/10 transition-colors duration-500" />
                </RevealImage>
                <div className="mt-5 flex items-baseline justify-between">
                  <div>
                    <h3 className="font-display text-2xl md:text-3xl tracking-widest2">
                      {p.title}
                    </h3>
                    <div className="mt-1 text-xs tracking-widest2 uppercase text-smoke">
                      {p.typology} — {p.location}
                    </div>
                  </div>
                  <span className="font-display text-xl text-smoke group-hover:text-ink transition-colors">
                    0{i + 1}
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>

        <Link to="/works" className="btn-link mt-12 md:hidden">
          <span>All works</span>
          <span aria-hidden>→</span>
        </Link>
      </section>

      {/* CONTACT STRIP */}
      <section className="border-t border-mist">
        <div className="container-page py-24 md:py-40 grid grid-cols-1 md:grid-cols-12 gap-10">
          <Reveal className="md:col-span-4">
            <div className="eyebrow">Get in touch</div>
          </Reveal>
          <Reveal className="md:col-span-8" delay={0.1}>
            <h2 className="font-display text-3xl sm:text-4xl md:text-6xl leading-tight tracking-normal sm:tracking-widest2">
              Conversations<br />about your next place.
            </h2>
            <div className="mt-10 flex flex-col md:flex-row gap-6 md:gap-12 text-sm">
              <a href="mailto:contact@rcmnrm.co.in" className="btn-link">
                contact@rcmnrm.co.in
              </a>
              <a href="tel:+919426209548" className="btn-link">
                +91 94262 09548
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
